import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import config from '../../config';
import { resolveDashboardClientEnv } from '../../utils/environmentMode';

const clientEnv = resolveDashboardClientEnv();
const uploadDraftsByShipment = new Map();
const SHIPMENT_DOCUMENTS_UPDATED_EVENT = 'shipment-documents-updated';
const SHIPMENT_UPLOAD_DOC_TYPE = 'admin_message_attachment';
const FILE_UPLOAD_TIMEOUT_MS = 20000;
const FILE_UPLOAD_WATCHDOG_MS = 45000;
const FILE_UPLOAD_ABORT_MS = 25000;
const PRODUCTION_DASHBOARD_HOST = 'dashboard.gophr.app';
const PRODUCTION_FILES_HOST = 'https://api-files.gophr.app';

const isProductionDashboardBrowser = () =>
    typeof window !== 'undefined' &&
    window.location &&
    typeof window.location.hostname === 'string' &&
    (window.location.hostname === PRODUCTION_DASHBOARD_HOST || window.location.hostname.endsWith(`.${PRODUCTION_DASHBOARD_HOST}`));

const resolveProductionBrowserHost = (configuredHost, productionHost) => {
    if (!isProductionDashboardBrowser()) {
        return configuredHost;
    }

    if (typeof configuredHost === 'string' && configuredHost.includes('dev-api-')) {
        return productionHost;
    }

    return configuredHost || productionHost;
};

const normalizeApiFilesHost = hostValue => {
    const host = String(hostValue || '').trim();
    if (!host) {
        return '';
    }

    if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:' && host.startsWith('http://')) {
        const insecureHost = host.slice('http://'.length);
        const isLocalInsecureHost = insecureHost.startsWith('localhost') || insecureHost.startsWith('127.0.0.1');

        if (!isLocalInsecureHost) {
            return `https://${insecureHost}`;
        }
    }

    return host;
};

const getApiFilesHost = () => normalizeApiFilesHost(resolveProductionBrowserHost(config.config.api_files.host, PRODUCTION_FILES_HOST));

const getUploadHostAttempts = () => {
    const primaryHost = getApiFilesHost();
    const productionHost = normalizeApiFilesHost(PRODUCTION_FILES_HOST);

    if (!primaryHost) {
        return [];
    }

    const shouldTryProductionFallback =
        primaryHost !== productionHost &&
        (primaryHost.includes('dev-api-files') || isProductionDashboardBrowser());

    if (!shouldTryProductionFallback) {
        return [primaryHost];
    }

    return [primaryHost, productionHost];
};

const getUploadDraftKey = (shipmentId, shipmentFriendlyId) => `${shipmentId || 'unknown'}:${shipmentFriendlyId || 'unknown'}`;

const notifyShipmentDocumentsUpdated = (shipmentId, shipmentFriendlyId, uploadedUrl = '', uploadedFileName = '', uploadedDocType = SHIPMENT_UPLOAD_DOC_TYPE) => {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
        return;
    }

    window.dispatchEvent(new CustomEvent(SHIPMENT_DOCUMENTS_UPDATED_EVENT, {
        detail: {
            shipmentId: shipmentId || '',
            shipmentFriendlyId: shipmentFriendlyId || '',
            uploadedUrl: uploadedUrl || '',
            uploadedFileName: uploadedFileName || '',
            uploadedDocType: uploadedDocType || SHIPMENT_UPLOAD_DOC_TYPE,
            updatedAt: Date.now(),
        }
    }));
};

const extractUploadErrorMessage = error => {
    const responseData = error && error.response ? error.response.data : null;
    const statusCode = error && error.response && error.response.status ? error.response.status : null;
    const debugText = responseData
        ? (responseData.debug || responseData.details || '')
        : '';
    const serverMessage = responseData
        ? (responseData.message || responseData.error || '')
        : '';
    const networkMessage = !error.response && error.message ? error.message : '';
    const fallbackBodyText = responseData && typeof responseData === 'string'
        ? responseData
        : '';
    const finalMessage = debugText || serverMessage || fallbackBodyText || networkMessage || 'Upload failed';

    return {
        statusCode,
        message: finalMessage,
    };
};

const isTransientUploadingStatus = status => String(status || '').trim().toLowerCase().startsWith('uploading');

const FileUpload = ({ shipmentId, shipmentFriendlyId }) => {
    const uploadDraftKey = getUploadDraftKey(shipmentId, shipmentFriendlyId);
    const existingDraft = uploadDraftsByShipment.get(uploadDraftKey) || {};
    const initialDraftStatus = isTransientUploadingStatus(existingDraft.uploadStatus) ? '' : (existingDraft.uploadStatus || '');
    const [selectedFileName, setSelectedFileName] = useState(existingDraft.fileName || '');
    const [uploadStatus, setUploadStatus] = useState(initialDraftStatus);
    const [uploadedFileUrl, setUploadedFileUrl] = useState(existingDraft.uploadedFileUrl || '');
    const [isUploading, setIsUploading] = useState(false);
    const isUploadingRef = useRef(false);
    const fileInputRef = useRef(null);
    const isMountedRef = useRef(true);
    const activeAbortControllerRef = useRef(null);
    const activeAbortTimeoutRef = useRef(null);
    const activeWatchdogRef = useRef(null);

    useEffect(() => () => {
        isMountedRef.current = false;
        if (activeAbortTimeoutRef.current) {
            window.clearTimeout(activeAbortTimeoutRef.current);
            activeAbortTimeoutRef.current = null;
        }
        if (activeWatchdogRef.current) {
            window.clearTimeout(activeWatchdogRef.current);
            activeWatchdogRef.current = null;
        }
        if (activeAbortControllerRef.current) {
            activeAbortControllerRef.current.abort();
            activeAbortControllerRef.current = null;
        }
    }, []);

    useEffect(() => {
        const persistedStatus = isTransientUploadingStatus(uploadStatus) ? '' : uploadStatus;

        uploadDraftsByShipment.set(uploadDraftKey, {
            fileName: selectedFileName,
            uploadStatus: persistedStatus,
            uploadedFileUrl,
        });
    }, [selectedFileName, uploadDraftKey, uploadStatus, uploadedFileUrl]);

    const resetInputElement = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const stopFileUploadInteraction = event => {
        if (!event) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    };

    const uploadSelectedFile = async file => {
        if (isUploadingRef.current) {
            return;
        }

        if (!file) {
            setUploadStatus('No file selected');
            return;
        }

        if (!shipmentId) {
            setUploadStatus('Upload failed: missing shipment id');
            return;
        }

        if (!shipmentFriendlyId) {
            setUploadStatus('Upload failed: missing shipment friendly id');
            return;
        }

        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) {
            setUploadStatus('Upload failed: missing auth token');
            return;
        }

        // Browsers and the storage service can't display HEIC/HEIF; convert to JPEG before upload.
        let uploadFile = file;
        const isHeic = /image\/(heic|heif)/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '');
        if (isHeic) {
            try {
                setUploadStatus('Converting image...');
                const heic2any = (await import('heic2any')).default;
                const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
                const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
                const jpegName = (file.name || 'upload').replace(/\.(heic|heif)$/i, '.jpg');
                uploadFile = new File([jpegBlob], jpegName, { type: 'image/jpeg' });
            } catch (conversionError) {
                setUploadStatus('Could not convert this HEIC image. Please upload a JPG, PNG, or PDF.');
                resetInputElement();
                return;
            }
        }

        const uploadWithContract = async host => {
            const payload = new FormData();
            payload.append('bill_of_lading_file', uploadFile);

            const abortController = new AbortController();
            activeAbortControllerRef.current = abortController;
            if (activeAbortTimeoutRef.current) {
                window.clearTimeout(activeAbortTimeoutRef.current);
            }
            activeAbortTimeoutRef.current = window.setTimeout(() => {
                if (activeAbortControllerRef.current) {
                    activeAbortControllerRef.current.abort();
                }
            }, FILE_UPLOAD_ABORT_MS);

            const request = axios({
                method: 'post',
                headers: {
                    Authorization: 'Bearer ' + accessToken,
                    'content-type': 'multipart/form-data',
                },
                url: `${host}/shipments/${shipmentId}/bol/upload?client_env=${clientEnv}&shipment_friendly_id=${shipmentFriendlyId || ''}&doc_type=${encodeURIComponent(SHIPMENT_UPLOAD_DOC_TYPE)}`,
                data: payload,
                timeout: FILE_UPLOAD_TIMEOUT_MS,
                signal: abortController.signal,
            });

            let hardTimeoutId = null;
            const hardTimeout = new Promise((_, reject) => {
                hardTimeoutId = window.setTimeout(() => {
                    reject(Object.assign(new Error('Upload timed out'), { code: 'UPLOAD_TIMEOUT' }));
                }, FILE_UPLOAD_TIMEOUT_MS + 1000);
            });

            return Promise.race([request, hardTimeout]).finally(() => {
                if (hardTimeoutId) {
                    window.clearTimeout(hardTimeoutId);
                }
            });
        };

        const uploadHosts = getUploadHostAttempts();
        const resolvedUploadHost = uploadHosts[0] || '';
        let lastAttemptedHost = resolvedUploadHost;

        if (activeWatchdogRef.current) {
            window.clearTimeout(activeWatchdogRef.current);
        }

        const watchdogId = window.setTimeout(() => {
            if (isUploadingRef.current) {
                if (activeAbortControllerRef.current) {
                    activeAbortControllerRef.current.abort();
                }
                if (isMountedRef.current) {
                    setUploadStatus('Upload stalled. Please try again.');
                    setIsUploading(false);
                }
                isUploadingRef.current = false;
                resetInputElement();
            }
        }, FILE_UPLOAD_WATCHDOG_MS);
        activeWatchdogRef.current = watchdogId;

        try {
            setIsUploading(true);
            isUploadingRef.current = true;
            setUploadStatus('Uploading...');

            if (!resolvedUploadHost) {
                throw new Error('Upload host is not configured');
            }

            let uploaded = false;
            let latestError = null;
            let successfulResponse = null;

            for (let i = 0; i < uploadHosts.length; i += 1) {
                const uploadHost = uploadHosts[i];
                lastAttemptedHost = uploadHost;

                try {
                    const attemptResponse = await uploadWithContract(uploadHost);
                    successfulResponse = attemptResponse;
                    uploaded = true;
                    break;
                } catch (attemptError) {
                    latestError = attemptError;
                    const isNetworkError = attemptError && !attemptError.response;
                    const hasMoreHosts = i < uploadHosts.length - 1;

                    if (!isNetworkError || !hasMoreHosts) {
                        throw attemptError;
                    }
                }
            }

            if (!uploaded && latestError) {
                throw latestError;
            }

            setSelectedFileName(uploadFile.name || 'Uploaded file');
            const uploadedUrl = successfulResponse && successfulResponse.data ? successfulResponse.data.url : '';
            const addedToShipmentsDb = Boolean(successfulResponse && successfulResponse.data && successfulResponse.data.added_to_shipments_db);

            if (!addedToShipmentsDb) {
                throw new Error('Uploaded file did not persist to shipment documents. Please try again.');
            }

            setUploadedFileUrl(uploadedUrl || '');
            setUploadStatus('Upload successful');
            notifyShipmentDocumentsUpdated(shipmentId, shipmentFriendlyId, uploadedUrl || '', uploadFile.name || 'Uploaded file', SHIPMENT_UPLOAD_DOC_TYPE);
            resetInputElement();
        } catch (error) {
            const isAbortError =
                (error && error.code === 'ERR_CANCELED') ||
                (error && error.name === 'CanceledError') ||
                (typeof axios.isCancel === 'function' && axios.isCancel(error));

            if (error && (error.code === 'ECONNABORTED' || error.code === 'UPLOAD_TIMEOUT' || isAbortError)) {
                setUploadStatus('Upload timed out. Please try again.');
                setUploadedFileUrl('');
                resetInputElement();
                return;
            }

            const details = extractUploadErrorMessage(error);
            const hostLabel = lastAttemptedHost ? ` [host: ${lastAttemptedHost}]` : '';
            setUploadStatus(details.statusCode ? `Upload failed (${details.statusCode}): ${details.message}${hostLabel}` : `Upload failed: ${details.message}${hostLabel}`);
            setUploadedFileUrl('');
            resetInputElement();
        } finally {
            window.clearTimeout(watchdogId);
            if (activeWatchdogRef.current === watchdogId) {
                activeWatchdogRef.current = null;
            }
            if (activeAbortTimeoutRef.current) {
                window.clearTimeout(activeAbortTimeoutRef.current);
                activeAbortTimeoutRef.current = null;
            }
            activeAbortControllerRef.current = null;
            if (isMountedRef.current) {
                setIsUploading(false);
            }
            isUploadingRef.current = false;
        }
    };

    const handleFileChange = event => {
        if (event) {
            event.stopPropagation();
        }

        const file = event && event.target && event.target.files ? event.target.files[0] : null;
        resetInputElement();

        if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
            setSelectedFileName(file.name || 'Selected file');
            setUploadStatus('');
            setUploadedFileUrl('');
            void uploadSelectedFile(file);
        } else {
            setSelectedFileName('');
            setUploadStatus('Only PDF or image files are allowed');
            setUploadedFileUrl('');
            resetInputElement();
        }
    };

    const openFilePicker = () => {
        if (isUploading) {
            return;
        }

        if (!fileInputRef.current) {
            return;
        }

        fileInputRef.current.value = '';
        fileInputRef.current.click();
    };

    const inputId = `shipment-file-upload-${shipmentId || 'unknown'}-${shipmentFriendlyId || 'unknown'}`;

    return (
        <div className="dispatch-realtime-interaction" style={{ borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    onMouseDown={event => event.stopPropagation()}
                    onClick={event => {
                        stopFileUploadInteraction(event);
                        openFilePicker();
                    }}
                    disabled={isUploading}
                    style={{
                        display: 'inline-block',
                        padding: '5px 10px',
                        color: '#fff',
                        borderRadius: '4px',
                        border: '1px solid #666',
                        background: '#555',
                        marginBottom: 0,
                        cursor: isUploading ? 'not-allowed' : 'pointer',
                        opacity: isUploading ? 0.7 : 1,
                    }}
                >
                    {isUploading ? 'Uploading...' : 'Upload File'}
                </button>
                <span style={{ color: '#fff', wordBreak: 'break-word' }}>{selectedFileName || 'No file selected'}</span>
                <input
                    id={inputId}
                    ref={fileInputRef}
                    type="file"
                    onClick={event => {
                        event.stopPropagation();
                        event.target.value = '';
                    }}
                    onChange={handleFileChange}
                    accept="application/pdf,image/*"
                    style={{
                        position: 'absolute',
                        opacity: 0,
                        pointerEvents: 'none',
                        width: '1px',
                        height: '1px'
                    }}
                />
            </div>
            {uploadStatus && (
                <p style={{ color: uploadStatus.startsWith('Upload successful') ? '#32CD32' : uploadStatus.startsWith('Uploading') ? '#1E90FF' : '#ff6b6b', marginTop: '10px' }}>{uploadStatus}</p>
            )}
        </div>
    );
};

export default FileUpload;
